import { useMemo, useRef, useEffect } from 'react'
import { useMessageStore } from './useMessageStore'

// TODO generalize hook name for use with any stream of items from Convex (sort order!!)
// TODO ... as opposed to a state update of one Convex record

type Message = {
  _creationTime: number
}

/**
 * Hook to filter sorted messages to only those newer than a tracked timestamp (Convex always provides)
 * Automatically updates the timestamp as new messages arrive
 * Initial default start time is "always" (e.g. time === 0)
 *
 * When gameId is provided, uses global Zustand store for timestamp tracking (recommended).
 * Without gameId, uses local component ref (legacy behavior).
 */
export function useNewMessages<T extends Message>(
  messages: T[] | undefined,
  gameId?: string,
  startTime = 0
): T[] {
  const localRef = useRef<number>(startTime)

  // Filter to only new messages (pure computation, no side effects)
  const newMessages = useMemo(() => {
    if (!messages?.length) return []

    // Get last timestamp — if no stored timestamp, treat all existing messages as already seen
    const storedTimestamp = gameId
      ? useMessageStore.getState().getLastSeen(gameId)
      : localRef.current || 0
    const lastTimestamp = storedTimestamp || messages[0]?._creationTime || startTime

    const filtered: T[] = []

    // Chat messages are sorted newest first (descending _creationTime)
    // Optimization: break early once we hit an old message
    for (const msg of messages) {
      if (msg._creationTime > lastTimestamp) {
        filtered.push(msg)
      } else {
        // Once we hit an old message, all remaining are older - can break early
        break
      }
    }

    return filtered
  }, [messages, gameId, startTime])

  // Update timestamp when newest message changes (side effect in useEffect)
  const newestTimestamp = messages?.[0]?._creationTime ?? 0

  useEffect(() => {
    if (newestTimestamp === 0) return

    if (gameId) {
      useMessageStore.getState().updateLastSeen(gameId, newestTimestamp)
    } else {
      localRef.current = newestTimestamp
    }
  }, [newestTimestamp, gameId])

  return newMessages
}
